/**
 * Data-backed <title> builder for /{make}/{model}/{dataType}.
 *
 * GSC evidence (Jul 27 – Aug 23 2026): curb-weight pages hold positions 7-10
 * on "how much does X weigh" / "X weight" with a 0% CTR, and engine pages took
 * 1,091 impressions with zero clicks. The titles were pure noun phrases with
 * no year, no number, and — on drivetrain — zero lexical overlap with the
 * query that drives the impressions ("powertrain options", 860 impressions).
 *
 * So every title here does three things where the data allows:
 *   1. leads with the model year,
 *   2. carries the actual measured value (weight, displacement, MPG),
 *   3. includes the synonym real searchers type (powertrain, gas mileage,
 *      engine size, gearbox).
 *
 * Every value is interpolated from loader rows at request time. Nothing is
 * hardcoded per page; when a row is missing the builder degrades to the next
 * shortest candidate and finally to the static label.
 */

type Row = any;

import { getDataTypeInfo, type DataTypeKey } from "@/lib/vehicle-data";

/** pageTitle() appends " | AutoSpecData" (15 chars) inside a 60-char budget. */
const MAX_BASE = 45;

export interface SpecTitleInput {
  fullName: string;
  latestYear: number | null;
  vehicles: Row[];
  specs: Row[];
  dimensions?: Row[];
  safety?: Row[];
}

/** First candidate that fits the title budget; otherwise the shortest, hard-truncated to the budget. */
function fit(candidates: string[]): string {
  const hit = candidates.find((c) => c.length <= MAX_BASE) ?? candidates[candidates.length - 1]!;
  return hit.length > MAX_BASE ? hit.slice(0, MAX_BASE - 1).trimEnd() + "…" : hit;
}

const num = (n: number) => Math.round(n).toLocaleString("en-US");

/** Range across rows, collapsed to a single value when lo === hi. */
function range(values: number[]): { lo: number; hi: number } | null {
  const clean = values.filter((v) => Number.isFinite(v) && v > 0);
  if (!clean.length) return null;
  return { lo: Math.min(...clean), hi: Math.max(...clean) };
}

function weightLabel(rows: Row[], year: number | null): string | null {
  const scoped = year ? rows.filter((d) => d.year === year) : rows;
  const r = range((scoped.length ? scoped : rows).map((d) => Number(d.curb_weight_lbs)));
  if (!r) return null;
  return r.lo === r.hi ? `${num(r.lo)} lbs` : `${num(r.lo)}–${num(r.hi)} lbs`;
}

function engineLabel(rows: Row[], year: number | null): string | null {
  const scoped = year ? rows.filter((v) => v.year === year) : rows;
  const src = scoped.length ? scoped : rows;
  const disp = range(src.map((v) => Number(v.displacement)));
  const cyl = Array.from(new Set(src.map((v) => v.cylinders).filter(Boolean))).map(String);
  const L = (v: number) => `${v.toFixed(1)}L`;
  const dispPart = disp ? (disp.lo === disp.hi ? L(disp.lo) : `${L(disp.lo)}–${L(disp.hi)}`) : null;
  const cylPart = cyl.length === 1 ? `${cyl[0]}-Cyl` : null;
  if (dispPart && cylPart) return `${dispPart} ${cylPart}`;
  return dispPart ?? (cylPart ? cylPart : null);
}

function mpgLabel(rows: Row[], year: number | null): string | null {
  const scoped = year ? rows.filter((v) => v.year === year) : rows;
  const src = scoped.length ? scoped : rows;
  const r = range(src.map((v) => Number(v.mpg_combined)));
  if (!r) return null;
  return r.lo === r.hi ? `${r.lo} MPG` : `${r.lo}–${r.hi} MPG`;
}

/** Highest published tow rating across rows, e.g. "3,500 lbs". */
function towLabel(rows: Row[], year: number | null): string | null {
  const scoped = year ? rows.filter((r) => r.year === year) : rows;
  const src = scoped.length ? scoped : rows;
  const max = Math.max(0, ...src.map((r) => Number(r.towing_capacity_lbs) || 0));
  return max > 0 ? `${num(max)} lbs` : null;
}

function driveLabel(rows: Row[], year: number | null): string | null {
  const scoped = year ? rows.filter((v) => v.year === year) : rows;
  const src = scoped.length ? scoped : rows;
  const short: Record<string, string> = {
    "front-wheel drive": "FWD",
    "rear-wheel drive": "RWD",
    "all-wheel drive": "AWD",
    "4-wheel drive": "4WD",
    "four-wheel drive": "4WD",
    "part-time 4-wheel drive": "4WD",
    "2-wheel drive": "2WD",
  };
  const list = Array.from(
    new Set(
      src
        .map((v) => (v.drive ? String(v.drive).toLowerCase() : null))
        .filter(Boolean)
        .map((d) => short[d as string] ?? null)
        .filter(Boolean) as string[],
    ),
  );
  return list.length ? list.slice(0, 3).join("/") : null;
}

/**
 * NHTSA overall star rating for the latest year we hold. GSC shows
 * "<model> safety rating" and "<model> crash test rating" ranking at pos 3-10
 * with a 0% CTR — the star count is the answer and it was missing from the
 * title entirely.
 */
function starLabel(rows: Row[], year: number | null): { stars: number; year: number } | null {
  const scoped = year ? rows.filter((s) => s.year === year) : rows;
  const src = scoped.length ? scoped : rows;
  const stars = src.map((s) => Number(s.overall_rating)).filter((n) => Number.isFinite(n) && n >= 1 && n <= 5);
  if (!stars.length) return null;
  const best = Math.max(...stars);
  const years = src
    .filter((s) => Number.isFinite(Number(s.overall_rating)))
    .map((s) => Number(s.year))
    .filter((n) => Number.isFinite(n));
  return { stars: best, year: years.length ? Math.max(...years) : (year ?? 0) };
}

/**
 * Length × width for the dimensions title, plus the year the figures actually
 * come from — a "2027" prefix on 2025 measurements is a snippet that lies.
 * Weight is deliberately excluded: /curb-weight owns weight queries.
 */
function sizeLabel(rows: Row[], year: number | null): { text: string; year: number | null } | null {
  const scoped = year ? rows.filter((d) => d.year === year) : rows;
  const src = scoped.length ? scoped : rows;
  const len = range(src.map((d) => Number(d.length_in)));
  const wid = range(src.map((d) => Number(d.width_in)));
  if (!len) return null;
  const one = (r: { lo: number; hi: number }) =>
    r.lo === r.hi ? `${Math.round(r.lo)}` : `${Math.round(r.lo)}–${Math.round(r.hi)}`;
  const years = src.map((d) => Number(d.year)).filter((n) => Number.isFinite(n));
  return {
    text: wid ? `${one(len)}″ L × ${one(wid)}″ W` : `${one(len)}″ Long`,
    year: years.length ? Math.max(...years) : year,
  };
}

/** Fuel tank in gallons; searchers type "gas tank size" far more than "capacity". */
function tankLabel(rows: Row[], year: number | null): string | null {
  const scoped = year ? rows.filter((s) => s.year === year) : rows;
  const src = scoped.length ? scoped : rows;
  const r = range(src.map((s) => Number(s.fuel_tank_gal)));
  if (!r) return null;
  const g = (v: number) => (Number.isInteger(v) ? `${v}` : v.toFixed(1));
  return r.lo === r.hi ? `${g(r.lo)} Gal` : `${g(r.lo)}–${g(r.hi)} Gal`;
}

/**
 * Title base (no brand suffix — pageTitle() adds it).
 * `yearPrefix` is the year already forced by a ?year= URL, if any.
 */
export function buildSpecTitleBase(dataType: DataTypeKey, input: SpecTitleInput, yearPrefix?: number): string {
  const info = getDataTypeInfo(dataType);
  const { fullName } = input;
  const dims = input.dimensions ?? [];
  const year = yearPrefix ?? input.latestYear ?? null;
  const y = year ? `${year} ` : "";
  const fallback = `${y}${fullName} ${info.title}`;
  const plain = `${fullName} ${info.title}`;

  switch (dataType) {
    case "curb-weight": {
      const w = weightLabel(dims, year);
      if (!w) return fit([fallback, `${fullName} Curb Weight — How Much It Weighs`, plain]);
      return fit([
        `${y}${fullName} Curb Weight: ${w}`,
        `${fullName} Curb Weight: ${w}`,
        `${y}${fullName} Curb Weight`,
        plain,
      ]);
    }
    case "engine": {
      const e = engineLabel(input.vehicles, year);
      if (!e) return fit([`${y}${fullName} Engine Specs & Engine Size`, fallback, plain]);
      return fit([
        `${y}${fullName} Engine Specs: ${e}`,
        `${fullName} Engine Specs: ${e}`,
        `${y}${fullName} Engine Specs & Size`,
        plain,
      ]);
    }
    case "drivetrain": {
      const d = driveLabel(input.vehicles, year);
      if (!d) return fit([`${y}${fullName} Drivetrain & Powertrain Options`, fallback, plain]);
      return fit([
        `${y}${fullName} Drivetrain & Powertrain: ${d}`,
        `${fullName} Drivetrain & Powertrain: ${d}`,
        `${y}${fullName} Drivetrain & Powertrain`,
        `${fullName} Drivetrain & Powertrain`,
        plain,
      ]);
    }
    case "mpg": {
      const m = mpgLabel(input.vehicles, year);
      if (!m) return fit([`${y}${fullName} MPG & Gas Mileage`, fallback, plain]);
      return fit([
        `${y}${fullName} MPG & Gas Mileage: ${m}`,
        `${fullName} MPG & Gas Mileage: ${m}`,
        `${y}${fullName} MPG & Gas Mileage`,
        `${fullName} MPG & Gas Mileage`,
        plain,
      ]);
    }
    case "safety-ratings": {
      const s = starLabel(input.safety ?? [], year);
      if (!s) return fit([`${y}${fullName} Safety Rating & Crash Test`, fallback, plain]);
      const sy = s.year ? `${s.year} ` : y;
      return fit([
        `${sy}${fullName} Safety Rating: ${s.stars} Stars (NHTSA)`,
        `${sy}${fullName} Safety Rating: ${s.stars} Stars`,
        `${fullName} Safety Rating: ${s.stars} Stars`,
        `${sy}${fullName} Safety Rating`,
        plain,
      ]);
    }
    case "dimensions": {
      // No "weight" here on purpose: /curb-weight owns weight queries, and
      // both URLs were surfacing for "how much does a X weigh".
      const s = sizeLabel(dims, year);
      if (!s) return fit([`${y}${fullName} Dimensions: Length, Width & Height`, fallback, plain]);
      const dy = s.year ? `${s.year} ` : y;
      return fit([
        `${dy}${fullName} Dimensions: ${s.text}`,
        `${fullName} Dimensions: ${s.text}`,
        `${dy}${fullName} Dimensions, Length & Width`,
        `${fullName} Dimensions, Length & Width`,
        plain,
      ]);
    }
    case "fuel-tank-capacity": {
      const t = tankLabel(input.specs, year);
      if (!t) return fit([`${y}${fullName} Gas Tank Size & Fuel Capacity`, fallback, plain]);
      return fit([
        `${y}${fullName} Gas Tank Size: ${t}`,
        `${fullName} Gas Tank Size: ${t}`,
        `${y}${fullName} Gas Tank Size & Fuel Capacity`,
        plain,
      ]);
    }
    case "towing-capacity": {
      const t = towLabel(input.specs, year);
      if (!t) return fit([`${y}${fullName} Towing Capacity & Tow Rating`, fallback, plain]);
      return fit([
        `${y}${fullName} Towing Capacity: ${t}`,
        `${fullName} Towing Capacity: ${t}`,
        `${y}${fullName} Towing Capacity & Tow Rating`,
        plain,
      ]);
    }
    case "transmission":
      return fit([
        `${y}${fullName} Transmission Specs & Gearbox Types`,
        `${fullName} Transmission Specs & Gearbox`,
        fallback,
        plain,
      ]);
    case "maintenance-schedule":
      // A model-year variant must never collapse to the bare-model title —
      // that produced two URLs sharing one <title>. Keep the year, shorten
      // the noun phrase instead.
      if (yearPrefix) {
        return fit([
          `${y}${fullName} Maintenance Schedule & Service Intervals`,
          `${y}${fullName} Maintenance Schedule & Service`,
          `${y}${fullName} Maintenance Schedule`,
          `${y}${fullName} Service Schedule`,
        ]);
      }
      return fit([fallback, plain]);
    default:
      return fit([fallback, plain]);
  }
}

/**
 * Question-form lead for the meta description, matching how the query is
 * actually typed ("how much does a Toyota Highlander weigh"). Only used where
 * GSC shows question-form queries hitting the template.
 */
export function specQuestionLead(
  dataType: DataTypeKey,
  input: {
    fullName: string;
    latestYear: number | null;
    /** Rows behind the page, so the snippet year matches the figures quoted. */
    safety?: Row[];
    dimensions?: Row[];
  },
): string | null {
  const rowYear = (rows: Row[] | undefined) => {
    const years = (rows ?? []).map((r) => Number(r.year)).filter((n) => Number.isFinite(n));
    return years.length ? Math.max(...years) : null;
  };
  const yr = (override: number | null) => {
    const v = override ?? input.latestYear;
    return v ? `${v} ` : "";
  };
  const y = yr(null);
  switch (dataType) {
    case "curb-weight":
      return `How much does a ${y}${input.fullName} weigh?`;
    case "engine":
      return `What engine is in the ${y}${input.fullName}?`;
    case "mpg":
      return `What gas mileage does the ${y}${input.fullName} get?`;
    case "safety-ratings":
      return `How safe is the ${yr(rowYear(input.safety))}${input.fullName}, and what did it score in NHTSA crash test ratings?`;
    case "dimensions":
      return `How long and how wide is the ${yr(rowYear(input.dimensions))}${input.fullName}?`;
    case "fuel-tank-capacity":
      return `How many gallons does a ${y}${input.fullName} hold, and what is the gas tank size?`;
    case "towing-capacity":
      return `How much can a ${y}${input.fullName} tow?`;

    default:
      return null;
  }
}
